// Global dialog for upserting FHIR resources. Render once at the app root.

import { formatAddress } from '@medplum/core'
import type { Resource, ResourceType } from '@medplum/fhirtypes'
import * as codeSystems from '@seen/core/codeSystems'
import * as patients from '@seen/core/models/patients'
import { cleanHumanNames } from '@seen/core/utils'
import type { JSX } from 'react'
import { toast } from 'sonner'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { ResourceForm } from '#src/components/medplum/ResourceForm.tsx'
import { useFhirCreateMutation } from '#src/hooks/useFhirCreateMutation.ts'
import { useFhirUpsertMutation } from '#src/hooks/useFhirUpsertMutation.ts'
import { useUpsertResourceFormDialogStore } from '#src/hooks/useUpsertResourceFormDialogStore.ts'

export type EditingResourceConfig<T extends Resource> = {
  fields: (keyof T)[]
  validate: (resource: T) => Error[]
  transform?: (resource: T) => T
  staticFieldValues?: Partial<T>
}

type EditingResourceConfigMap = {
  [K in ResourceType]?: EditingResourceConfig<
    Extract<Resource, { resourceType: K }>
  >
}

const editingResourceConfigMap: EditingResourceConfigMap = {
  Practitioner: {
    fields: ['name', 'telecom'],
    validate: (resource) => {
      const errors: Error[] = []
      if (!resource.name?.length) {
        errors.push(new Error('Name is required'))
      }
      if (!resource.telecom?.length) {
        errors.push(new Error('Telecom is required'))
      }
      return errors
    },
    transform: (resource) => ({
      ...resource,
      name: cleanHumanNames(resource.name),
    }),
    staticFieldValues: {
      active: true,
    },
  },
  Location: {
    fields: ['name', 'description', 'address', 'telecom'],
    validate: (resource) => {
      const errors: Error[] = []
      if (!resource.name?.length) {
        errors.push(new Error('Name is required'))
      }
      if (!resource.address || !formatAddress(resource.address)) {
        errors.push(new Error('Address is required'))
      }
      return errors
    },
    // Stamp the address-derived dedup identifier that `ExtendedLocation.build`
    // sets, so hand-made Locations converge with roster- and provider-derived
    // ones instead of becoming an invisible duplicate of the same building.
    // Editing the address re-derives it: a stale hash would keep matching the
    // prior address, so materializing the new one would create a duplicate.
    transform: (resource) => {
      const value = patients.addressIdentifier(resource.address)
      if (!value) {
        return resource
      }
      return {
        ...resource,
        identifier: [
          ...(resource.identifier ?? []).filter(
            (i) => i.system !== codeSystems.LOCATION_SYSTEM
          ),
          { system: codeSystems.LOCATION_SYSTEM, value },
        ],
      }
    },
    staticFieldValues: {
      status: 'active',
    },
  },
}

export function UpsertResourceFormDialog(): JSX.Element | null {
  const isOpen = useUpsertResourceFormDialogStore((s) => s.isOpen)
  const resource = useUpsertResourceFormDialogStore((s) => s.resource)
  const title = useUpsertResourceFormDialogStore((s) => s.title)
  const onSubmit = useUpsertResourceFormDialogStore((s) => s.onSubmit)
  const overrideVisibleFields = useUpsertResourceFormDialogStore(
    (s) => s.visibleFields
  )
  const overrideValidate = useUpsertResourceFormDialogStore((s) => s.validate)
  const overrideTransform = useUpsertResourceFormDialogStore((s) => s.transform)
  const overrideStaticFieldValues = useUpsertResourceFormDialogStore(
    (s) => s.staticFieldValues
  )
  const setIsOpen = useUpsertResourceFormDialogStore((s) => s.setIsOpen)
  const reset = useUpsertResourceFormDialogStore((s) => s.reset)

  const resourceType = (
    resource && 'resourceType' in resource ? resource.resourceType : 'Basic'
  ) as ResourceType

  // Get config for this resource type
  const config = editingResourceConfigMap[resourceType]

  const visibleFields = overrideVisibleFields ?? config?.fields
  const validate = overrideValidate ?? config?.validate
  const transform = overrideTransform ?? config?.transform
  const staticFieldValues =
    overrideStaticFieldValues ?? config?.staticFieldValues

  const createMutation = useFhirCreateMutation(resourceType, {
    mutation: {
      onSuccess: (resource) => {
        onSubmit?.(resource as any)
        reset()
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Unknown error')
      },
    },
  })
  const upsertMutation = useFhirUpsertMutation(resourceType, {
    mutation: {
      onSuccess: (resource) => {
        onSubmit?.(resource as any)
        reset()
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Unknown error')
      },
    },
  })

  // Only render if open and resource is set
  if (!isOpen || !resource) {
    return null
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogDisplay>
        <DialogHeader>
          <DialogTitle>
            {title ?? `${resource.id ? 'Edit' : 'Create'} ${resourceType}`}
          </DialogTitle>
        </DialogHeader>
        <DialogContent>
          <ResourceForm
            defaultValue={resource}
            loading={upsertMutation.isPending || createMutation.isPending}
            visibleFields={visibleFields as any}
            onSubmit={(r) => {
              if (!('resourceType' in resource)) {
                return
              }
              const transformedResource = transform ? transform(r as any) : r
              const errors = validate?.(transformedResource as any) ?? []
              if (errors?.length) {
                toast.error(
                  errors.map((e) => <p key={e.message}>{e.message}</p>),
                  { duration: 5_000 }
                )
                return
              }
              if (transformedResource.id) {
                upsertMutation.mutate({
                  resource: {
                    ...transformedResource,
                    ...staticFieldValues,
                  } as any,
                  query: {
                    _id: transformedResource.id,
                  },
                })
              } else {
                createMutation.mutate({
                  ...transformedResource,
                  ...staticFieldValues,
                } as any)
              }
            }}
          />
        </DialogContent>
      </DialogDisplay>
    </Dialog>
  )
}
